import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  X,
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  Phone,
  Globe,
  Trophy,
  Shirt,
  Loader,
} from "lucide-react";
import { getDatabase, ref, set, get, update } from "../../Config/matuRtdb";
import { getAuth } from "../../Config/matuAuth";

const ConfigModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [totalSteps] = useState(4);
  const [isSaving, setIsSaving] = useState(false);
  const auth = getAuth();

  // Estado para los datos del usuario
  const [userData, setUserData] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    favoriteTeam: "",
    favoriteLeague: "",
  });

  // Opciones para selects
  const countries = [
    "Colombia",
    "España",
    "México",
    "Argentina",
    "Chile",
    "Perú",
    "Ecuador",
    "Estados Unidos",
    "Brasil",
    "Otro",
  ];
  const leagues = [
    "La Liga",
    "Premier League",
    "Serie A",
    "Bundesliga",
    "Ligue 1",
    "Liga BetPlay",
    "Liga MX",
    "Copa Libertadores",
    "Champions League",
    "Otra",
  ];
  const teams = [
    // España
    "Real Madrid",
    "Barcelona",
    "Atlético Madrid",
    "Sevilla",
    "Valencia",
    // Inglaterra
    "Manchester United",
    "Liverpool",
    "Chelsea",
    "Manchester City",
    "Arsenal",
    // Italia
    "Juventus",
    "Inter",
    "Milan",
    "Napoli",
    "Roma",
    // Alemania
    "Bayern Munich",
    "Borussia Dortmund",
    "RB Leipzig",
    "Bayer Leverkusen",
    // Francia
    "PSG",
    "Lyon",
    "Marseille",
    // Colombia
    "Atlético Nacional",
    "América de Cali",
    "Millonarios",
    "Junior",
    "Deportivo Cali",
    // Otros populares
    "Boca Juniors",
    "River Plate",
    "Flamengo",
    "Palmeiras",
  ];

  // Cargar datos del usuario al abrir el modal
  useEffect(() => {
    if (isOpen && auth.currentUser) {
      const db = getDatabase();
      const userRef = ref(db, `usuarios/${auth.currentUser.uid}`);

      get(userRef)
        .then((snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            setUserData({
              name: data.name || auth.currentUser.displayName || "",
              email: auth.currentUser.email || "",
              phone: data.phone || "",
              country: data.country || "",
              favoriteTeam: data.favoriteTeam || "",
              favoriteLeague: data.favoriteLeague || "",
            });
          } else {
            // Si no hay datos previos, usar los del auth
            setUserData({
              name: auth.currentUser.displayName || "",
              email: auth.currentUser.email || "",
              phone: "",
              country: "",
              favoriteTeam: "",
              favoriteLeague: "",
            });
          }
        })
        .catch((error) => {
          console.error("Error loading user data:", error);
        });
    }
  }, [isOpen, auth]);

  // Guardar datos en Firebase preservando TODOS los campos existentes
  const saveUserData = () => {
    if (auth.currentUser) {
      setIsSaving(true);
      const db = getDatabase();
      const userRef = ref(db, `usuarios/${auth.currentUser.uid}`);

      // Primero obtenemos los datos actuales para preservar todos los campos
      get(userRef)
        .then((snapshot) => {
          let existingData = {};

          // Si existen datos previos, guardarlos todos
          if (snapshot.exists()) {
            existingData = snapshot.val();
          }

          // Preparamos los nuevos datos manteniendo todos los campos existentes
          // y actualizando solo los que necesitamos cambiar
          const updatedData = {
            ...existingData, // Mantener TODOS los campos existentes (incluyendo typeU)
            name: userData.name,
            email: userData.email, // Aunque no sea editable, lo mantenemos
            phone: userData.phone,
            country: userData.country,
            favoriteTeam: userData.favoriteTeam,
            favoriteLeague: userData.favoriteLeague,
          };

          // Actualizamos los datos preservando todos los campos
          set(userRef, updatedData) // Usamos set con los datos completos
            .then(() => {
              console.log(
                "Datos actualizados correctamente preservando todos los campos"
              );
              setTimeout(() => {
                setIsSaving(false);
                onClose();
              }, 1000); // Mostramos el loader por 1 segundo antes de cerrar
            })
            .catch((error) => {
              console.error("Error updating data:", error);
              setIsSaving(false);
            });
        })
        .catch((error) => {
          console.error("Error fetching current data:", error);
          setIsSaving(false);
        });
    }
  };

  // Manejar cambios en los inputs
  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Ir al siguiente paso
  const nextStep = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      saveUserData();
    }
  };

  // Ir al paso anterior
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Si el modal no está abierto, no renderizar nada
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        className="bg-gradient-to-br from-blue-900 to-purple-900 rounded-xl shadow-xl w-full max-w-md overflow-hidden"
      >
        {/* Cabecera */}
        <div className="p-4 border-b border-blue-700 flex justify-between items-center">
          <h2 className="text-xl font-bold text-white">Configuración</h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className={`p-1 rounded-full hover:bg-blue-800 transition-colors ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <X size={20} className="text-gray-300" />
          </button>
        </div>

        {/* Indicador de progreso */}
        <div className="px-4 pt-4">
          <div className="flex justify-between mb-2">
            {Array.from({ length: totalSteps }).map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 mx-1 rounded-full ${
                  step > i ? "bg-blue-400" : "bg-gray-600"
                }`}
              />
            ))}
          </div>
          <p className="text-gray-300 text-sm text-center">
            Paso {step} de {totalSteps}
          </p>
        </div>

        {/* Contenido del formulario */}
        <div className="p-4">
          {isSaving ? (
            <div className="flex flex-col items-center justify-center py-10">
              <Loader size={40} className="text-blue-400 animate-spin mb-4" />
              <p className="text-white text-lg">Guardando cambios...</p>
            </div>
          ) : (
            <>
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-blue-800 rounded-full">
                      <User size={24} className="text-blue-200" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center">
                    Información básica
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Nombre
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={userData.name}
                        onChange={handleChange}
                        className="w-full p-3 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Tu nombre"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Email (no editable)
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={userData.email}
                        disabled
                        className="w-full p-3 bg-blue-800 bg-opacity-30 rounded-lg border border-blue-700 text-gray-400 focus:outline-none cursor-not-allowed"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-blue-800 rounded-full">
                      <Phone size={24} className="text-blue-200" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center">
                    Datos de contacto
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Número de teléfono
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={userData.phone}
                      onChange={handleChange}
                      className="w-full p-3 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="+57 300 123 4567"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Formato recomendado: +[código de país] [número]
                    </p>
                  </div>
                </motion.div>
              )}

              {step === 3 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-blue-800 rounded-full">
                      <Globe size={24} className="text-blue-200" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center">
                    Ubicación
                  </h3>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      País
                    </label>
                    <select
                      name="country"
                      value={userData.country}
                      onChange={handleChange}
                      className="w-full p-3 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Selecciona un país</option>
                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>
                </motion.div>
              )}

              {step === 4 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-4"
                >
                  <div className="flex items-center justify-center mb-4">
                    <div className="p-3 bg-blue-800 rounded-full">
                      <Shirt size={24} className="text-blue-200" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-white text-center">
                    Preferencias deportivas
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Equipo favorito
                      </label>
                      <select
                        name="favoriteTeam"
                        value={userData.favoriteTeam}
                        onChange={handleChange}
                        className="w-full p-3 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecciona un equipo</option>
                        {teams.map((team) => (
                          <option key={team} value={team}>
                            {team}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-1">
                        Liga favorita
                      </label>
                      <select
                        name="favoriteLeague"
                        value={userData.favoriteLeague}
                        onChange={handleChange}
                        className="w-full p-3 bg-blue-800 bg-opacity-50 rounded-lg border border-blue-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Selecciona una liga</option>
                        {leagues.map((league) => (
                          <option key={league} value={league}>
                            {league}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </motion.div>
              )}
            </>
          )}
        </div>

        {/* Botones de navegación */}
        <div className="p-4 border-t border-blue-700 flex justify-between">
          {step > 1 ? (
            <button
              onClick={prevStep}
              disabled={isSaving}
              className={`px-4 py-2 flex items-center space-x-1 text-white bg-blue-800 hover:bg-blue-700 rounded-lg transition-colors ${
                isSaving ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              <ChevronLeft size={16} />
              <span>Anterior</span>
            </button>
          ) : (
            <div></div>
          )}

          <button
            onClick={nextStep}
            disabled={isSaving}
            className={`px-4 py-2 flex items-center space-x-1 text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-lg transition-colors ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <span>{step === totalSteps ? "Guardar" : "Siguiente"}</span>
            {step === totalSteps ? (
              <Check size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default ConfigModal;
